import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RoutesList } from '../../../config/routes-list';
import { useAnalytics } from '../../../hooks/analytics/use-analytics';
import { useNotification } from '../../../hooks/use-notification';
import { GoodsReceivingService } from '../../../services/goods-receiving/goods-receiving.service';
import type {
  DeliveryNote,
  DuplicateCheckResponse,
  IGoodsReceivingService,
  UpdateDraftDto,
} from '../../../services/goods-receiving/goods-receiving.types';
import { getHttpErrorStatus, resolveApiErrorMessage } from '../../../utils/error.utils';
import { goodsReceivingQueryKeys } from '../query-keys';

export function useCreateDraft() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: () => service.createDraft(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goodsReceivingQueryKeys.drafts() });
      trackEvent('ddt_create', { method: 'manual' });
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useDeleteDraft() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: (id: string) => service.deleteDraft(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: goodsReceivingQueryKeys.draft(id) });
      queryClient.invalidateQueries({ queryKey: goodsReceivingQueryKeys.drafts() });
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function usePatchDraft() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showError, showWarning } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDraftDto }) =>
      service.updateDraft(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: goodsReceivingQueryKeys.drafts() });
      queryClient.invalidateQueries({ queryKey: goodsReceivingQueryKeys.draft(variables.id) });
    },
    onError: (err: unknown) => {
      // PATCHing this endpoint returns 400 when the receipt has already left
      // DRAFT state. This is a stale-workflow conflict, not a generic save
      // failure: keep the internal UUID out of the UI and offer the right exit.
      if (getHttpErrorStatus(err) === 400) {
        showWarning(t('draftNoLongerEditable'));
        navigate(`${RoutesList.GOODS_RECEIVING}/history`, { replace: true });
        return;
      }
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useCheckDuplicate() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);

  return useMutation<DuplicateCheckResponse, unknown, string>({
    mutationFn: (draftId: string) => service.checkDuplicate(draftId),
  });
}

export function useAddPhotos() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();

  // NOTE: no onError here on purpose. The only consumer
  // (supplier-photo-extraction) awaits `mutateAsync` and owns the failure UX
  // (network vs rate-limit vs file-too-large vs technical) in its own catch.
  // Adding a toast here would double up with that message.
  return useMutation({
    mutationFn: ({
      draftId,
      files,
      options,
    }: {
      draftId: string;
      files: File[];
      options?: { extract?: boolean; appendPages?: boolean };
    }) => service.addPhotos(draftId, files, options),
    onSuccess: (_, variables) => {
      trackEvent('ddt_photo_upload', { ai_extraction: !!variables.options?.extract });
      if (variables.options?.extract) {
        queryClient.setQueryData<DeliveryNote | null | undefined>(
          goodsReceivingQueryKeys.draft(variables.draftId),
          (old) => (old ? { ...old, extractionStatus: 'PENDING' } : old),
        );
      }
      queryClient.invalidateQueries({ queryKey: goodsReceivingQueryKeys.draft(variables.draftId) });
    },
  });
}

export function useDeletePhoto() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: ({ deliveryNoteId, photoId }: { deliveryNoteId: string; photoId: string }) =>
      service.deletePhoto(deliveryNoteId, photoId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.draft(variables.deliveryNoteId),
      });
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}
